import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useConnectionStore } from '../connection';
import { API, Util } from '@vp/common';
import { useStorage } from '@vueuse/core';
import { ApolloClient } from '@apollo/client/core';

const ACCOUNT_ID_KEY = 'SELECTED_ORGANIZATION_ID';

type Organization = API.App.Account.Organization.Organization;

interface OrganizationsApi {
  fetchOrganizations: (client: ApolloClient) => Promise<Organization[]>;
}

export const useOrganizationStore = (Api: OrganizationsApi, name: string) =>
  defineStore(name, () => {
    const connectionStore = useConnectionStore();

    const organizations = ref<Organization[]>([]);
    const accountsLoading = ref<Util.LoadingState>();
    const selectedAccountId = useStorage<number | undefined>(ACCOUNT_ID_KEY, undefined, undefined, {
      serializer: {
        read: (value: string) => {
          const parsed = parseInt(value, 10);
          return isNaN(parsed) ? undefined : parsed;
        },
        write: (value: number | undefined) => value?.toString() ?? '',
      },
    });

    const loadOrganizations = async ({ setDefault } = { setDefault: false }) => {
      try {
        accountsLoading.value = Util.LoadingState.LOADING;
        organizations.value = await Api.fetchOrganizations(connectionStore.gqlApiClient);
        accountsLoading.value = Util.LoadingState.DONE;

        if (setDefault && !selectedAccount.value) {
          const selected = defaultAccount.value;
          if (selected) {
            selectedAccountId.value = selected.id;
          }
        }
      } catch (e) {
        accountsLoading.value = Util.LoadingState.ERROR;
        throw e;
      }
    };

    const setSelectedAccountId = (id: number) => {
      selectedAccountId.value = id;
    };

    const selectedAccount = computed(() => {
      if (selectedAccountId.value) {
        return organizations.value.find(account => account.id === selectedAccountId.value);
      }
      return undefined;
    });

    const defaultAccount = computed(() => organizations.value.find(({ default: isDefault }) => isDefault) ?? organizations.value[0]);

    return {
      organizations,
      accountsLoading,
      loadOrganizations,

      defaultAccount,

      setSelectedAccountId,
      selectedAccount,
      selectedAccountId,
    };
  })();
